import type { FC } from "react";
import { useState } from "react";
import { createPortal } from "react-dom";

import classNames from "classnames";

import { ChevronDownIcon } from "@jti/brand-icons/chevron-down";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import { useHeaderContext } from "../../context";
import { HeaderDesktopNavItem } from "../HeaderDesktopNavItem";
import { PointsDisplay } from "../PointsDisplay";

import styles from "./Navigation.module.scss";

export type NavLinks = Array<NavLink>;
export type NavLink = {
  title: string;
  url: string;
  secondaryNavigation?: {
    id: string;
  };
  isActive?: boolean;
  isLoyalty?: boolean;
  loyaltyPointsCurrency?: string;
  loyaltyPoints?: number | null;
  chevronAriaLabel?: string;
};

export type NavigationProps = {
  navLinks: NavLinks;
  renderSubmenu?: (id: string, isDarkMode?: boolean) => JSX.Element;
  storeLocatorLink?: string;
};

export const Navigation: FC<NavigationProps> = ({ navLinks, renderSubmenu, storeLocatorLink }) => {
  const [openedIndex, setOpenedIndex] = useState<number | null>(null);
  const { hoveredIndex, setHoveredIndex } = useHeaderContext();

  const isSubmenuOpen = hoveredIndex !== null && Boolean(navLinks[hoveredIndex]?.secondaryNavigation);

  const handleClick = (index: number) => {
    setOpenedIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  const handleMouseEnter = (index: number) => {
    setHoveredIndex(index);
  };

  const handleMouseLeave = () => {
    setHoveredIndex(null);
  };

  const dispatchGTMEvent = (title: string, url: string) => {
    dispatchTrackingEvent(TrackingEvents.TrackingMenuNavigationClick, { menu_link: title });
    if (storeLocatorLink && url.includes(storeLocatorLink)) {
      dispatchTrackingEvent(TrackingEvents.TrackingStoreLocatorClick);
    }
  };

  return (
    <nav className={styles.container}>
      {typeof document !== "undefined" &&
        createPortal(
          <div aria-hidden className={classNames(styles.backdrop, { [styles.backdropVisible]: isSubmenuOpen })} />,
          document.body
        )}
      <ul className={styles.list}>
        {navLinks.map(
          (
            { title, url, secondaryNavigation, isActive, isLoyalty, loyaltyPointsCurrency, chevronAriaLabel },
            index
          ) => {
            const hasSubMenu = Boolean(secondaryNavigation);

            return (
              <li key={`${title}-${url}`} onMouseEnter={() => handleMouseEnter(index)} onMouseLeave={handleMouseLeave}>
                <div className={styles.listItem}>
                  <HeaderDesktopNavItem
                    href={url}
                    label={title}
                    badge={isLoyalty ? <PointsDisplay loyaltyCurrency={loyaltyPointsCurrency ?? ""} /> : undefined}
                    hasSecondaryNavigation={hasSubMenu}
                    isOpen={hoveredIndex === index}
                    chevronIcon={hasSubMenu ? ChevronDownIcon : undefined}
                    onChevronClick={() => handleClick(index)}
                    chevronAriaLabel={chevronAriaLabel}
                    title={title}
                    onClick={() => dispatchGTMEvent(title, url)}
                    aria-current={isActive ? "page" : undefined}
                  />
                </div>
                {hasSubMenu && (
                  <div
                    className={classNames(styles.submenu, {
                      [styles.isHovered]: hoveredIndex === index,
                      [styles.submenuOpened]: openedIndex === index,
                    })}
                  >
                    {renderSubmenu?.(secondaryNavigation?.id ?? "")}
                  </div>
                )}
              </li>
            );
          }
        )}
      </ul>
    </nav>
  );
};
